import { useCallback, useEffect, useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { ApiError, del, fmtBytes, fmtDateTime, get, html, patch, post, put } from "../lib.js";
import { Badge, Empty, Field, Loading, Modal, PageHeader, toast, useAction, useLoad, usePoll } from "../ui.js";
import { PageGuide } from "../guide.js";

const STATUS = {
  received: { label: tr("Waiting"), kind: "muted" },
  transcribing: { label: tr("Transcribing…"), kind: "accent" },
  summarizing: { label: tr("Summarizing…"), kind: "accent" },
  ready: { label: tr("Ready to review"), kind: "warn" },
  approved: { label: tr("Approved"), kind: "ok" },
  failed: { label: tr("Failed"), kind: "warn" },
};
const WORKING = ["received", "transcribing", "summarizing"];

/** The browser often has no type for .amr or .m4a; the API wants audio/*, so name it from the extension. */
const AUDIO_TYPES = { m4a: "audio/mp4", mp4: "audio/mp4", mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg", opus: "audio/ogg", webm: "audio/webm", amr: "audio/amr" };

const statusBadge = (s) => html`<${Badge} kind=${STATUS[s]?.kind ?? "neutral"}>${STATUS[s]?.label ?? s}</${Badge}>`;
const fmtDuration = (sec) => (sec ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}` : "");

/** Seconds of audio, read by the browser before upload; null when it can't tell. */
function readDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (v) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    audio.preload = "metadata";
    audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
    audio.onerror = () => done(null);
    setTimeout(() => done(null), 4000);
    audio.src = url;
  });
}

async function uploadRecording(t, file) {
  const ext = file.name.toLowerCase().split(".").pop();
  const type = file.type.startsWith("audio/") ? file.type : AUDIO_TYPES[ext];
  if (!type) throw new Error(tr("Choose an audio recording: m4a, mp3, wav, ogg, webm or amr."));
  const duration = await readDuration(file);
  const params = new URLSearchParams({ filename: file.name, ...(duration ? { duration: String(duration) } : {}) });
  const res = await fetch(`/api${t.base}/calls/upload?${params}`, { method: "POST", credentials: "same-origin", headers: { "content-type": type }, body: file });
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? tr("The upload failed. Try again."));
  return data.recording;
}

export function CallsPage({ t }) {
  const [shown, setShown] = useState(50);
  const [list, reload] = useLoad(() => get(`${t.base}/calls?limit=${shown}`), [t.id, shown]);
  const [open, setOpen] = useState(null);
  // While anything is being worked on, look again every few seconds.
  const working = list?.recordings.some((r) => WORKING.includes(r.status));
  usePoll(useCallback(reload, [t.id, shown]), working ? 5000 : 30_000);
  return html`<div>
    <${PageHeader} title=${tr("Call notes")} subtitle=${tr("Upload a call recording; get a summary in your template to review and approve")} />
    <${PageGuide} id="calls" />
    ${!list && html`<${Loading} />`}
    ${list && !list.available && html`<div class="callout">${tr("Call notes aren't switched on for this platform yet.")}</div>`}
    ${list?.available && html`<${UploadCard} t=${t} limits=${list.limits} onUploaded=${(r) => { reload(); setOpen(r.id); }} />`}
    ${list && html`<section class="card">
      <h2>${tr("Recordings")}</h2>
      ${!list.recordings.length && html`<${Empty} title=${tr("No recordings yet")}>${tr("Upload a recording above. Its summary appears here when it is ready, usually within a minute or two.")}</${Empty}>`}
      ${list.recordings.length > 0 && html`<ul class="plain email-rows">${list.recordings.map((r) => html`<li class="email-row">
        <div class="email-main">
          <button class="link" onClick=${() => setOpen(r.id)}><strong dir="auto">${r.title || r.original_filename}</strong></button>
          <div class="muted small" dir="auto">${r.contact_name || r.contact_phone || tr("No contact linked")}${r.duration_sec ? ` · ${fmtDuration(r.duration_sec)}` : ""}</div>
          ${r.status === "failed" && r.error && html`<div class="muted small" dir="auto">${r.error}</div>`}
        </div>
        <div class="email-meta">
          ${statusBadge(r.status)}
          <span class="nowrap small muted">${fmtDateTime(r.created_at, t.timezone)}</span>
        </div>
      </li>`)}</ul>`}
      ${list.has_more && html`<p class="center"><button class="btn small ghost" onClick=${() => setShown((n) => n + 50)}>${tr("Show more")}</button></p>`}
    </section>`}
    ${list?.available && t.role !== "agent" && html`<${TemplateCard} t=${t} />`}
    ${open && html`<${CallDetail} t=${t} id=${open} onClose=${() => setOpen(null)} onChange=${reload} />`}
  </div>`;
}

function UploadCard({ t, limits, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > limits.max_bytes) return toast(tr("The recording is larger than {n} MB.", { n: Math.round(limits.max_bytes / (1024 * 1024)) }), "error");
    setBusy(true);
    try {
      const rec = await uploadRecording(t, file);
      toast(tr("Uploaded. Transcribing now."));
      onUploaded(rec);
    } catch (err) {
      toast(err.message ?? String(err), "error");
    } finally {
      setBusy(false);
    }
  };
  return html`<section class="card">
    <h2>${tr("Upload a recording")}</h2>
    <p class="small">${tr("Pick a call recording from your phone or computer (up to {n} MB). It is transcribed and summarized in your template, and nothing is sent to the customer.", { n: Math.round(limits.max_bytes / (1024 * 1024)) })}</p>
    <label class="btn primary">
      ${busy ? tr("Uploading…") : tr("Choose a recording")}
      <input type="file" accept="audio/*,.m4a,.amr,.opus" hidden disabled=${busy} onChange=${onPick} />
    </label>
    <${PhoneCapture} />
    <p class="muted small">${tr("Recording calls, and telling the other side, is your business's responsibility.")}</p>
    <p class="muted small">${tr("The audio is deleted after {n} days; the transcript and summary stay until you delete the recording.", { n: limits.audio_retention_days })}</p>
  </section>`;
}

/**
 * In the Android app only: this phone's automatic call capture (⋮ > Call recordings in the quick
 * view), read from and opened through the app's bridge. The app tells the page when it changes with
 * a "relayza:capture" event. Hidden on the web and in app versions from before the bridge had it.
 */
const CAPTURE_LABELS = {
  manual: () => tr("Manual"),
  known_contacts: () => tr("Automatic, contacts only"),
  all: () => tr("Automatic, all calls"),
};

function PhoneCapture() {
  const bridge = typeof window !== "undefined" ? window.RelayzaApp : undefined;
  const supported = typeof bridge?.getCallCapture === "function" && typeof bridge?.openCallCapture === "function";
  const [mode, setMode] = useState(() => (supported ? bridge.getCallCapture() : null));
  useEffect(() => {
    if (!supported) return;
    const onChange = (e) => setMode(e.detail ?? bridge.getCallCapture());
    window.addEventListener("relayza:capture", onChange);
    return () => window.removeEventListener("relayza:capture", onChange);
  }, [supported]);
  if (!supported) return null;
  return html`<p class="small phone-capture">
    ${tr("Automatic upload from this phone:")} <strong>${(CAPTURE_LABELS[mode] ?? CAPTURE_LABELS.manual)()}</strong>
    ${" · "}<button class="link" onClick=${() => bridge.openCallCapture()}>${tr("Change")}</button>
  </p>`;
}

function TemplateCard({ t }) {
  const [data] = useLoad(() => get(`${t.base}/calls/template`), [t.id]);
  const [value, setValue] = useState(null);
  const [busy, run] = useAction();
  const text = value ?? data?.template ?? "";
  const save = () => run(() => put(`${t.base}/calls/template`, { template: text }), tr("Saved"));
  return html`<section class="card">
    <h2>${tr("Summary template")}</h2>
    <p class="muted small">${tr("The sections you want in every call summary, one per line. Left empty, summaries use the default shown in the box.")}</p>
    ${!data && html`<${Loading} />`}
    ${data && html`
      <textarea dir="auto" rows="6" value=${text} placeholder=${data.default} onInput=${(e) => setValue(e.target.value)}></textarea>
      <div class="row-actions">
        <button class="btn primary" disabled=${busy} onClick=${save}>${tr("Save template")}</button>
        ${!text.trim() && html`<button class="btn ghost" onClick=${() => setValue(data.default)}>${tr("Start from the default")}</button>`}
      </div>`}
  </section>`;
}

/** Search the business's contacts and pick one. */
function ContactPicker({ t, current, onPick, disabled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) return setResults([]);
    let alive = true;
    const id = setTimeout(() => get(`${t.base}/contacts?${new URLSearchParams({ q: q.trim() })}`).then((d) => alive && setResults(d.contacts.slice(0, 8))).catch(() => undefined), 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [q]);
  return html`<div>
    <p class="small">${current ? html`<strong dir="auto">${current}</strong>` : html`<span class="muted">${tr("No contact linked")}</span>`}</p>
    <input dir="auto" type="search" placeholder=${tr("Search by name or phone")} value=${q} disabled=${disabled} onInput=${(e) => setQ(e.target.value)} />
    ${results.length > 0 && html`<ul class="plain">${results.map((c) => html`<li>
      <button class="link" disabled=${disabled} onClick=${() => { onPick(c.id); setQ(""); setResults([]); }}><span dir="auto">${c.name || tr("No name")}</span> <span class="muted small" dir="ltr">${c.phone ?? ""}</span></button>
    </li>`)}</ul>`}
  </div>`;
}

function CallDetail({ t, id, onClose, onChange }) {
  const [data, reload] = useLoad(() => get(`${t.base}/calls/${id}`), [id]);
  const r = data?.recording;
  usePoll(useCallback(reload, [id]), r && WORKING.includes(r.status) ? 4000 : 60_000);
  const [text, setText] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, run] = useAction();
  useEffect(() => {
    setText(null);
    setPicked({});
  }, [r?.status]);
  const done = () => {
    reload();
    onChange();
  };
  const summaryText = text ?? r?.summary_text ?? "";
  const editable = r && (r.status === "ready" || r.status === "approved");
  const linkContact = async (contact_id) => {
    await run(() => patch(`${t.base}/calls/${id}`, { contact_id }), tr("Contact linked"));
    done();
  };
  const saveText = async () => {
    await run(() => patch(`${t.base}/calls/${id}`, { summary_text: summaryText }), tr("Saved"));
    done();
  };
  const approve = async () => {
    const tasks = Object.entries(picked).filter(([, on]) => on).map(([index]) => ({ index: Number(index) }));
    const res = await run(() => post(`${t.base}/calls/${id}/approve`, { summary_text: summaryText, tasks }));
    toast(res.task_ids.length ? tr("Approved, {n} tasks created", { n: res.task_ids.length }) : tr("Approved"));
    if (res.task_ids.length) window.dispatchEvent(new Event("rz:attention"));
    done();
  };
  const retry = async () => {
    await run(() => post(`${t.base}/calls/${id}/retry`), tr("Trying again"));
    done();
  };
  const remove = async () => {
    if (!confirm(tr("Delete this recording, its transcript and its summary? Tasks already created stay."))) return;
    await run(() => del(`${t.base}/calls/${id}`), tr("Recording deleted"));
    onChange();
    onClose();
  };
  const copy = () => navigator.clipboard.writeText(summaryText).then(() => toast(tr("Copied")));
  const items = r?.summary?.action_items ?? [];
  const anyPicked = Object.values(picked).some(Boolean);
  return html`<${Modal} title=${r?.title || tr("Call notes")} onClose=${onClose} wide>
    ${!r && html`<${Loading} />`}
    ${r && html`
      <p class="small">${statusBadge(r.status)} <span class="muted" dir="auto">${r.original_filename}</span> · <span class="muted">${fmtDateTime(r.created_at, t.timezone)}</span>${r.duration_sec ? html` · <span class="muted">${fmtDuration(r.duration_sec)}</span>` : ""} · <span class="muted">${fmtBytes(r.bytes)}</span></p>
      ${WORKING.includes(r.status) && html`<div class="callout small">${tr("Working on it. This page updates by itself.")}</div>`}
      ${r.status === "failed" && html`<div class="callout error small" dir="auto">${r.error}</div>
        <p><button class="btn small" disabled=${busy || (!r.audio_kept && !r.transcript)} onClick=${retry}>${tr("Retry")}</button></p>`}
      <${Field} label=${tr("Contact")}>
        <${ContactPicker} t=${t} current=${r.contact_name || r.contact_phone} disabled=${busy} onPick=${linkContact} />
      </${Field}>
      ${editable && html`
        <h3>${tr("Summary")}</h3>
        <textarea dir="auto" rows="14" value=${summaryText} readonly=${r.status !== "ready"} disabled=${busy} onInput=${(e) => setText(e.target.value)}></textarea>
        <div class="row-actions">
          ${r.status === "ready" && text !== null && text !== r.summary_text && html`<button class="btn" disabled=${busy} onClick=${saveText}>${tr("Save changes")}</button>`}
          <button class="btn ghost" onClick=${copy}>${tr("Copy")}</button>
        </div>
        ${items.length > 0 && html`<h3>${tr("Follow-ups from the call")}</h3>
          <ul class="plain">${items.map((it, i) => html`<li class="checks-inline">
            <label>
              ${r.status === "ready" && html`<input type="checkbox" checked=${!!picked[i]} disabled=${busy} onChange=${(e) => setPicked((p) => ({ ...p, [i]: e.target.checked }))} />`}
              <span dir="auto">${it.text}${it.owner ? ` · ${it.owner}` : ""}${it.due ? ` · ${it.due}` : ""}</span>
            </label>
          </li>`)}</ul>
          ${r.status === "ready" && html`<p class="muted small">${r.contact_id ? tr("Ticked follow-ups become tasks on the contact when you approve.") : tr("Link a contact to turn follow-ups into tasks.")}</p>`}`}
        ${r.status === "ready" && html`<p><button class="btn primary" disabled=${busy || !summaryText.trim() || (anyPicked && !r.contact_id)} onClick=${approve}>${tr("Approve summary")}</button></p>`}
        ${r.status === "approved" && html`<p class="muted small">${tr("Approved {when}", { when: fmtDateTime(r.approved_at, t.timezone) })}</p>`}`}
      ${r.transcript && html`<details>
        <summary>${tr("Transcript")}</summary>
        <pre class="email-body" dir="auto">${r.transcript}</pre>
      </details>`}
      <hr />
      <button class="btn small ghost" disabled=${busy} onClick=${remove}>${tr("Delete recording")}</button>`}
  </${Modal}>`;
}
